import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/header';

import './EmployeeProfile.css';

const steps = [
    { key: 'submitted', label: 'Claim Submitted' },
    { key: 'sent', label: 'Sent to Insurer' },
    { key: 'review', label: 'Insurer Review' },
    { key: 'final', label: 'Claim Result' },
];


const EmployeeProfile = () => {
    const navigate = useNavigate();
    const [employee, setEmployee] = useState(null);
    const [claims, setClaims] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [trackedClaimId, setTrackedClaimId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [schemeData, setSchemeData] = useState(null);
    const [familyConfig, setFamilyConfig] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const fieldLabels = {
        emp_id: "Employee ID",
        emp_email_id: "Email",
        emp_name: "Name",
        joining_date: "Joining Date",
        designation: "Designation",
        phone_number: "Phone Number",
        gender: "Gender",
        dob: "Date of Birth",
        age: "Age",
        sum_insured: "Sum Insured",
        sum_insured_bal: "Sum Insured Balance",
        policy_start_date: "Policy Start Date",
        policy_end_date: "Policy End Date",
    };
    const formatDependentsDOB = (dependents) => {
        const formatted = {};
        for (let key in dependents) {
            formatted[key] = {
                ...dependents[key],
                dob: dependents[key].dob ? dependents[key].dob.split('T')[0] : ''
            };
        }
        return formatted;
    };

const isWithin30Days = (date) => {
    if (!date) return true;

    const selectedDate = new Date(date);
    const today = new Date();

    const diffTime = today - selectedDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    return diffDays <= 30;
};




    const [dependents, setDependents] = useState({
        spouse: { name: '', dob: '' },
        child1: { name: '', dob: '' },
        child2: { name: '', dob: '' },
        child3: { name: '', dob: '' },
        father: { name: "", dob: "" },
        mother: { name: "", dob: "" },
        father_inlaw: { name: "", dob: "" },
        mother_inlaw: { name: "", dob: "" }
    });
    const [originalDependents, setOriginalDependents] = useState({});
    const dateFields = ["dob", "joining_date", "policy_start_date", "policy_end_date"];
    const [showDependentPopup, setShowDependentPopup] = useState(false);
    const calculateAge = (dob) => {
        if (!dob) return "N/A";
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const isParentEmail = (email) => {
        if (!email) return false;
        const lowerEmail = email.toLowerCase();
        return lowerEmail.includes("father") || lowerEmail.includes("mother") || lowerEmail.includes("parent");
    };

    useEffect(() => {
        const fetchScheme = async () => {
            if (!employee?.emp_id || !employee?.org_id) return;

            try {
                const res = await fetch(
                    `/api/employee-scheme/${employee.emp_id}/${employee.org_id}`
                );

                if (!res.ok) return;

                const result = await res.json();

                if (result.success) {
                    setSchemeData(result.data);   // ✅ IMPORTANT FIX
                }

            } catch (err) {
                console.error("Error fetching scheme:", err);
            }
        };

        fetchScheme();
    }, [employee?.emp_id, employee?.org_id]);


    // FETCH EMPLOYEE
    useEffect(() => {
        const fetchEmployee = async () => {
            try {
                const storedUser = localStorage.getItem("user");
                if (!storedUser) {
                    navigate("/login");
                    return;
                }

                const parsedUser = JSON.parse(storedUser);
                const userEmail = parsedUser.user_email_id;

                const res = await fetch(`/api/employees/email/${userEmail}`);
                if (!res.ok) throw new Error("Failed to fetch employee");

                const data = await res.json();
                setEmployee(data);
                setFormData(data);
            } catch (error) {
                console.error("Error fetching employee:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEmployee();
    }, [navigate]);


    // FETCH CLAIMS
    useEffect(() => {
        const fetchClaims = async () => {
            if (!employee?.emp_id || !employee?.org_id) return;

            try {
                const res = await fetch(
                    `/api/claim-request/employee/${employee.emp_id}/${employee.org_id}`
                );

                if (!res.ok) throw new Error("Failed to fetch claims");
                const data = await res.json();
                setClaims(data);
            } catch (err) {
                console.error("Error fetching claims:", err);
            }
        };

        fetchClaims();
    }, [employee?.emp_id, employee?.org_id]);

    useEffect(() => {
        const fetchFamilyConfig = async () => {
            if (!schemeData?.scheme?.scheme_id) return;

            try {
                const res = await fetch(
                    `/api/scheme-family/${schemeData.scheme.scheme_id}`
                );

                if (!res.ok) return;

                const data = await res.json();
                setFamilyConfig(data);

            } catch (err) {
                console.error("Error fetching family config", err);
            }
        };

        fetchFamilyConfig();
    }, [schemeData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const fetchDependents = async () => {
        try {
            const response = await fetch(`/api/dependents/${employee.emp_id}`);
            if (!response.ok) return;

            const data = await response.json();

            // Helper to format DOB for input type="date"
            const formatDOB = (dob) => (dob ? dob.split('T')[0] : '');

            const formatted = {
                spouse: { name: data.spouse?.name || '', dob: formatDOB(data.spouse?.dob) },
                child1: { name: data.child1?.name || '', dob: formatDOB(data.child1?.dob) },
                child2: { name: data.child2?.name || '', dob: formatDOB(data.child2?.dob) },
                child3: { name: data.child3?.name || '', dob: formatDOB(data.child3?.dob) },
                parents: { name: data.parents?.name || '', dob: formatDOB(data.parents?.dob) },
                inlaws: { name: data.inlaws?.name || '', dob: formatDOB(data.inlaws?.dob) },
            };

            setDependents(formatted);
            setOriginalDependents(formatted); // Store original for comparison
        } catch (err) {
            console.error("Failed to fetch dependents", err);
        }
    };
    const handleDependentChange = (type, field, value) => {
        setDependents(prev => ({
            ...prev,
            [type]: { ...prev[type], [field]: value },
        }));
    };

   const handleSendRequest = async () => {

    if (isSending) return; // Prevent double click

    try {

        setIsSending(true);

        const loggedUser = JSON.parse(localStorage.getItem("user"));

        if (!loggedUser?.org_id) {
            alert("Organization not found");
            setIsSending(false);
            return;
        }

        const today = new Date();

        // ---------- FILTER ONLY CHANGED DEPENDENTS ----------
        const filteredDependents = Object.fromEntries(
            Object.entries(dependents).filter(([key, value]) => {

                const original = originalDependents[key] || { name: "", dob: "" };

                return value.name !== original.name || value.dob !== original.dob;

            })
        );

        if (Object.keys(filteredDependents).length === 0) {
            alert("No new dependent data entered to send.");
            setIsSending(false);
            return;
        }

        // ---------- VALIDATE 30 DAYS ONLY FOR CHANGED DEPENDENTS ----------
        const validate30Days = (dob, relation) => {

            if (!dob) return true;

            const selectedDate = new Date(dob);
            const diffTime = today - selectedDate;
            const diffDays = diffTime / (1000 * 60 * 60 * 24);

            if (diffDays > 30) {
                alert(`${relation} can only be added within the last 30 days. Please contact Admin.`);
                return false;
            }

            return true;
        };

        for (const [relation, data] of Object.entries(filteredDependents)) {

            let relationName = relation;

            if (relation === "spouse") relationName = "Spouse";
            if (relation === "child1") relationName = "Child 1";
            if (relation === "child2") relationName = "Child 2";
            if (relation === "child3") relationName = "Child 3";

            if (data.dob && !validate30Days(data.dob, relationName)) {
                setIsSending(false);
                return;
            }
        }

        // ---------- EMPLOYEE PAYLOAD ----------
        const payloadEmployee = {
            emp_id: employee.emp_id,
            emp_name: employee.emp_name,
            org_id: Number(loggedUser.org_id)
        };

        // ---------- SEND REQUEST ----------
        const res = await fetch(
            "/api/send-dependent-request",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    employee: payloadEmployee,
                    dependents: filteredDependents
                })
            }
        );

        const data = await res.json();

        if (!res.ok) {
            alert(data.message || "Failed to send request");
            setIsSending(false);
            return;
        }

        alert("Request sent to admin successfully!");

        setShowDependentPopup(false);

    } catch (err) {

        console.error(err);
        alert("Server error");

    } finally {

        setIsSending(false);

    }

};


    const handleSave = async () => {
        if (!employee) return;

        const payload = {
            emp_name: formData.emp_name || employee.emp_name,
            designation: formData.designation || employee.designation,
            phone_number: formData.phone_number || employee.phone_number,
            dob: formData.dob || employee.dob,
            gender: formData.gender || employee.gender,
            joining_date: formData.joining_date || employee.joining_date,
        };

        try {
            const response = await fetch(`/api/employees/${employee.emp_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error('Failed to update employee');

            const updated = await response.json();
            setEmployee(updated);
            setFormData(updated);
            setIsEditing(false);
            alert("Employee updated successfully!");
        } catch (err) {
            console.error(err);
            alert("Error updating employee");
        }
    };

    const getStepColor = (claim, stepKey) => {
        switch (stepKey) {
            case 'submitted':
            case 'sent':
                return 'green';
            case 'review':
                return claim.status === 'Approved' ? 'green' : 'gray';
            case 'final':
                return claim.status === 'Approved' ? 'green' : 'red';
            default:
                return 'gray';
        }
    };

    if (loading) return <p>Loading...</p>;
    if (!employee) return <p>No employee found. Please login.</p>;

    return (
        <div className="employee-container">
            <aside className="sidebar">
                <Header />
            </aside>
            <div className="settings-content">

                {schemeData && (
                    <div className="benefits-row">

                        {/* ================= SCHEME SECTION ================= */}
                        <div className="benefit-section">
                            <h2 className="benefit-title">Scheme Benefits</h2>

                            <div className="credit-card scheme-card">
                                <div className="card-header">
                                    <h3>{schemeData.scheme?.scheme_name}</h3>
                                    <span>Scheme ID: {schemeData.scheme?.scheme_id}</span>
                                </div>

                                <div className="card-body">
                                    <p><strong>Insurer:</strong> {schemeData.scheme?.insurer}</p>
                                    <p><strong>TPA:</strong> {schemeData.scheme?.tpa}</p>
                                    <p><strong>Sum Insured:</strong> ₹{schemeData.scheme?.sum_insured}</p>
                                    <p>
                                        <strong>Valid:</strong>{" "}
                                        {schemeData.scheme?.start_date
                                            ? new Date(schemeData.scheme.start_date).toLocaleDateString()
                                            : "-"}
                                        {" - "}
                                        {schemeData.scheme?.end_date
                                            ? new Date(schemeData.scheme.end_date).toLocaleDateString()
                                            : "-"}
                                    </p>
                                </div>

                                {schemeData.scheme?.scheme_doc && (
                                    <a
                                        href={`/uploads/${schemeData.scheme.scheme_doc}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="card-link"
                                    >
                                        View Scheme Document
                                    </a>
                                )}
                            </div>
                        </div>


                        {/* ================= POLICY SECTION ================= */}
                        <div className="benefit-section">
                            <h2 className="benefit-title">Policy Benefits</h2>

                            <div className="credit-card policy-card">
                                <div className="card-header">
                                    <h3>{schemeData.policy?.policy_name}</h3>
                                    <span>Policy ID: {schemeData.policy?.policy_id}</span>
                                </div>

                                <div className="card-body">
                                    <p><strong>Company:</strong> {schemeData.policy?.company_name}</p>
                                    <p><strong>TPA Name:</strong> {schemeData.policy?.tpa_name}</p>
                                    <p><strong>Policy No:</strong> {schemeData.policy?.policy_number}</p>
                                    <p>
                                        <strong>Valid:</strong>{" "}
                                        {schemeData.policy?.start_date
                                            ? new Date(schemeData.policy.start_date).toLocaleDateString()
                                            : "-"}
                                        {" - "}
                                        {schemeData.policy?.end_date
                                            ? new Date(schemeData.policy.end_date).toLocaleDateString()
                                            : "-"}
                                    </p>
                                </div>

                                {schemeData.policy?.policy_document && (
                                    <a
                                        href={`/uploads/${schemeData.policy.policy_document}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="card-link"
                                    >
                                        View Policy Document
                                    </a>
                                )}
                            </div>
                        </div>

                    </div>
                )}
                <div className="top-action-bar">
                    <div className="top-action-buttons">
                        <button
  className="action-btn primary"
  onClick={() =>
    navigate("/employee-claim", {
      state: { emp_id: employee.emp_id, org_id: employee.org_id }
    })
  }
>
  Claim Request
</button>

                        <button
                            className="action-btn secondary"
                            onClick={() => {
                                setShowDependentPopup(true); // open popup
                                fetchDependents();           // load spouse data
                            }}
                        >
                            Add Dependents
                        </button>


                    </div>
                </div>

                {/* TOP HEADER CARD */}
                <div className="profile-card profile-top-card">

               
                    <div className="profile-top-grid">
                        <div className="profile-top-item">
                            <span className="label">Emp ID:</span>
                            <span className="data">{employee.emp_id || "N/A"}</span>
                        </div>
                        <div className="profile-top-item">
                            <span className="label">Designation:</span>
                            <span className="data">{employee.designation || "N/A"}</span>
                        </div>
                        <div className="profile-top-item">
                            <span className="label">Phone:</span>
                            <span className="data">{employee.phone_number || "N/A"}</span>
                        </div>
                        <div className="profile-top-item">
                            <span className="label">DOB:</span>
                            <span className="data">{employee.dob ? new Date(employee.dob).toLocaleDateString() : "N/A"}</span>
                        </div>
                    </div>
                </div>

                {/* EMPLOYEE NAME + ACTION */}
                <div className="profile-header">
                    <h2>{employee.emp_name}</h2>
                    <div className="profile-actions">
                        <button className="edit-btn" onClick={() => setIsEditing(!isEditing)}>
                            {isEditing ? "Cancel" : "Edit"}
                        </button>
                        {isEditing && (
                            <button className="save-btn-userrole" onClick={handleSave}>Save</button>
                        )}
                    </div>
                </div>

                {/* DETAILS GRID */}
                <div className="profile-card1">
                    <div className="profile-details-grid">
                        {Object.keys(fieldLabels).map(field => (
                            <div className="profile-field" key={field}>
                                <label>{fieldLabels[field]}</label>
                                {isEditing && !(field === "age" && isParentEmail(employee.emp_email_id)) ? (
                                    <input
                                        type="text"
                                        name={field}
                                        value={formData[field] || ""}
                                        onChange={handleChange}
                                    />
                                ) : (
                                    <span>
                                        {dateFields.includes(field)
                                            ? employee[field] ? new Date(employee[field]).toLocaleDateString() : "N/A"
                                            : field === "age" ? calculateAge(employee.dob)
                                                : employee[field] || "N/A"}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* CLAIMS TABLE */}
                <div className="profile-card1">
                    <h3 className="card-title">Claim Request Details</h3>
                    {claims.length > 0 ? (
                        <table className="claim-table">
                            <thead>
                                <tr>
                                    <th>Claim ID</th>
                                    <th>Claim Type</th>
                                    <th>Patient Name</th>
                                    <th>Relationship</th>
                                    <th>Admission Date</th>
                                    <th>Discharge Date</th>
                                    <th>Amount</th>
                                    <th>Track</th>
                                </tr>
                            </thead>
                            <tbody>
                                {claims.map(claim => (
                                    <React.Fragment key={claim.claim_req_id}>
                                        <tr>
                                            <td>{claim.claim_req_id}</td>
                                            <td>{claim.claim_type}</td>
                                            <td>{claim.patient_name}</td>
                                            <td>{claim.member_type}</td>
                                           <td>{new Date(claim.admission_date).toLocaleDateString()}</td>
<td>{new Date(claim.discharge_date).toLocaleDateString()}</td>
                                            <td>{claim.claim_amount}</td>
                                            <td>
                                                <button
                                                    className="track-btn"
                                                    onClick={() => setTrackedClaimId(trackedClaimId === claim.claim_req_id ? null : claim.claim_req_id)}
                                                >
                                                    Track
                                                </button>
                                            </td>
                                        </tr>
                                        {trackedClaimId === claim.claim_req_id && (
<tr>
<td colSpan="8">

<div className="claim-tracker">

{/* Submitted */}
<div className="claim-step">
<div className="claim-circle active"></div>
<div className="claim-label">Submitted</div>
</div>

<div className="claim-line active"></div>

{/* Sent */}
<div className="claim-step">
<div className="claim-circle active"></div>
<div className="claim-label">Sent</div>
</div>

<div className="claim-line"></div>

{/* Final Status */}
<div className="claim-step">

<div
className="claim-circle"
style={{
backgroundColor:
claim.claim_status === "Approved"
? "green"
: claim.claim_status === "Rejected"
? "red"
: "gray"
}}
></div>

<div className="claim-label">

{claim.claim_status === "Pending"
? "Decision Pending"
: claim.claim_status}

</div>

</div>

</div>

</td>
</tr>
)}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="no-claims">No claims found</p>
                    )}
                </div>
            </div>
            {showDependentPopup && (
                <div className="dependent-popup-overlay">
                    <div className="dependent-popup">
                        <h2>Add Dependents</h2>
                        <p className="popup-note">
                            For adding dependents, a request mail will be sent to Admin.
                        </p>

                        <div className="dependent-form">

                            {/* ================= Employee Details ================= */}
                            <h4>Employee Details</h4>
                            <div className="field-row">
                                <input
                                    type="text"
                                    value={employee.emp_id}
                                    readOnly
                                />
                                <input
                                    type="text"
                                    value={employee.emp_name}
                                    readOnly
                                />
                            </div>

                            {/* ================= SPOUSE ================= */}
                            {familyConfig?.family_spouse && (
                                <>
                                    <h4>Spouse</h4>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="Spouse Name"
                                            value={dependents.spouse?.name || ""}
                                            onChange={(e) =>
                                                handleDependentChange("spouse", "name", e.target.value)
                                            }
                                        />
                                        <h4>DOB</h4>
                                        <input
                                            type="date"
                                            value={dependents.spouse?.dob || ""}
                                            onChange={(e) =>
                                                handleDependentChange("spouse", "dob", e.target.value)
                                            }
                                        />
                                    </div>
                                </>
                            )}

                            {/* ================= CHILDREN ================= */}
                            {familyConfig?.family_child &&
                                Array.from({
                                    length: familyConfig.no_of_children || 0,
                                }).map((_, index) => {
                                    const key = `child${index + 1}`;
                                    return (
                                        <div key={key}>
                                            <h4>Child {index + 1}</h4>
                                            <div className="field-row">
                                                <input
                                                    type="text"
                                                    placeholder={`Child ${index + 1} Name`}
                                                    value={dependents[key]?.name || ""}
                                                    onChange={(e) =>
                                                        handleDependentChange(
                                                            key,
                                                            "name",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                 <h4>DOB</h4>
                                                <input
                                                    type="date"
                                                    value={dependents[key]?.dob || ""}
                                                    onChange={(e) =>
                                                        handleDependentChange(
                                                            key,
                                                            "dob",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                            {/* ================= PARENTS ================= */}
                            {familyConfig?.family_parents && (
                                <>
                                    <h4>Father</h4>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="Father Name"
                                            value={dependents.father?.name || ""}
                                            onChange={(e) =>
                                                handleDependentChange("father", "name", e.target.value)
                                            }
                                        />
                                         <h4>DOB</h4>
                                        <input
                                            type="date"
                                            value={dependents.father?.dob || ""}
                                            onChange={(e) =>
                                                handleDependentChange("father", "dob", e.target.value)
                                            }
                                        />
                                    </div>

                                    <h4>Mother</h4>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="Mother Name"
                                            value={dependents.mother?.name || ""}
                                            onChange={(e) =>
                                                handleDependentChange("mother", "name", e.target.value)
                                            }
                                        />
                                         <h4>DOB</h4>
                                        <input
                                            type="date"
                                            value={dependents.mother?.dob || ""}
                                            onChange={(e) =>
                                                handleDependentChange("mother", "dob", e.target.value)
                                            }
                                        />
                                    </div>
                                </>
                            )}

                            {/* ================= IN-LAWS ================= */}
                            {familyConfig?.family_inlaws && (
                                <>
                                    <h4>Father In-law</h4>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="Father In-law Name"
                                            value={dependents.father_inlaw?.name || ""}
                                            onChange={(e) =>
                                                handleDependentChange(
                                                    "father_inlaw",
                                                    "name",
                                                    e.target.value
                                                )
                                            }
                                        />
                                         <h4>DOB</h4>
                                        <input
                                            type="date"
                                            value={dependents.father_inlaw?.dob || ""}
                                            onChange={(e) =>
                                                handleDependentChange(
                                                    "father_inlaw",
                                                    "dob",
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <h4>Mother In-law</h4>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="Mother In-law Name"
                                            value={dependents.mother_inlaw?.name || ""}
                                            onChange={(e) =>
                                                handleDependentChange(
                                                    "mother_inlaw",
                                                    "name",
                                                    e.target.value
                                                )
                                            }
                                        />
                                         <h4>DOB</h4>
                                        <input
                                            type="date"
                                            value={dependents.mother_inlaw?.dob || ""}
                                            onChange={(e) =>
                                                handleDependentChange(
                                                    "mother_inlaw",
                                                    "dob",
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ================= ACTION BUTTONS ================= */}
                        <div className="popup-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setShowDependentPopup(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className={`btn-submits ${isSending ? "loading" : ""}`}
                                onClick={handleSendRequest}
                                disabled={isSending}
                            >
                                {isSending ? "Sending..." : "Send Request to Admin"}
                            </button>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeProfile;
